import LayoutHR from "layouts/layout-hr";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import MasterDataFormV2 from "../MasterDataFormV2";
import { ShiftsProvider, useShifts } from "./context/ShiftsContext";
import { getDevelopmentBanner } from "utils/devNotice";
import { useNavigate } from "react-router-dom";
import { deleteRequest } from "services/api";

function ShiftsContent() {
  const { shifts, filterOptions, handleSearch, handleSearchChange, refetch, Pagination } =
    useShifts();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const {
    // handleSuccess,
    handleError,
    // showCanvasHandler,
    handleSuccessToast,
    // handleLoadingThenSuccessToast,
  } = useCanvasHandler();

  // Handle create new shift
  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  // Handle edit shift
  const handleEdit = (item) => {
    setShowModal(true);
    setEditingItem(item);
  };

  // Handle delete shift
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/shifts/${id}`);
        if (response.success) {
          handleSuccessToast(true, "Shift deleted successfully");
          refetch(); // Refresh the data
        } else {
          handleError("Failed to delete shift");
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(true, "Shift saved successfully");
  };

  // Handle form error
  const handleFormError = (error) => {
    handleError(error);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Form fields configuration
  const formFields = [
    {
      name: "name",
      label: "Shift Name",
      type: "text",
      required: true,
      placeholder: "Enter shift name",
    },
    {
      name: "code",
      label: "Shift Code",
      type: "text",
      required: true,
      placeholder: "Enter shift code",
    },
    {
      name: "color",
      label: "Color",
      type: "color",
      required: true,
      placeholder: "Select color",
      defaultValue: "#000000",
    },
    {
      name: "start_time",
      label: "Start Time",
      type: "time",
      required: true,
      placeholder: "Select start time",
    },
    {
      name: "end_time",
      label: "End Time",
      type: "time",
      required: true,
      placeholder: "Select end time",
    },
    {
      name: "break_start",
      label: "Break Start",
      type: "time",
      required: false,
      placeholder: "Select break start time",
    },
    {
      name: "break_end",
      label: "Break End",
      type: "time",
      required: false,
      placeholder: "Select break end time",
    },
    {
      name: "break_duration",
      label: "Break Duration (minutes)",
      type: "number",
      required: true,
      placeholder: "Enter break duration",
      min: 0,
    },
    {
      name: "is_overnight",
      label: "Is Overnight Shift",
      type: "checkbox",
      required: false,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Enter shift description",
    },
  ];

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        {/* Page Header */}
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-arrow-left"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <line x1="5" y1="12" x2="11" y2="18" />
                  <line x1="5" y1="12" x2="11" y2="6" />
                </svg>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">Shift Management</h2>
              <div className="text-muted mt-1">
                Manage work shifts and schedules
              </div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Shift
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="input-icon">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search shifts..."
                    value={filterOptions.search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={handleSearch}
                  />
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-search"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <circle cx="10" cy="10" r="7" />
                      <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>Shift Name</th>
                    <th>Code</th>
                    <th>Color</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Break Start</th>
                    <th>Break End</th>
                    <th>Break Duration</th>
                    <th>Overnight</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.loading ? (
                    <tr>
                      <td colSpan="12" className="text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading shifts...
                      </td>
                    </tr>
                  ) : shifts.data?.length > 0 ? (
                    shifts.data.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          {(filterOptions.currentPage - 1) * 10 + index + 1}
                        </td>
                        <td>
                          <Dropdown style={{ position: 'relative', zIndex: 1050 }}>
                            <Dropdown.Toggle
                              variant="default"
                              size="sm"
                              id={`dropdown-${item.id}`}
                            >
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleEdit(item)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDelete(item.id)}
                                className="text-danger"
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>{item.name}</td>
                        <td>{item.code}</td>
                        <td>
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: item.color,
                              borderRadius: '4px',
                              display: 'inline-block'
                            }}
                          ></div>
                        </td>
                        <td>{item.start_time}</td>
                        <td>{item.end_time}</td>
                        <td>{item.break_start || '-'}</td>
                        <td>{item.break_end || '-'}</td>
                        <td>{item.break_duration ? `${item.break_duration} min` : '-'}</td>
                        <td>{item.is_overnight ? 'Yes' : 'No'}</td>
                        <td>{item.description}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="12" className="text-center">
                        No shifts found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {shifts.pagination && <Pagination />}
          </div>
        </div>

        {/* Modal for Create/Edit */}
        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingItem ? "Edit Shift" : "Create Shift"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/shifts"
                    initialData={editingItem}
                    fields={formFields}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutHR>
  );
}

export default function ShiftsPage() {
  return (
    <ShiftsProvider>
      <ShiftsContent />
    </ShiftsProvider>
  );
}